import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Header } from 'components/PaymentConsent/Header';
import { AddNewPaymentMethod } from 'components/PaymentConsent/AddNewPaymentMethod';
import { useAppDispatch, useTypedSelector } from 'combineReducers';
import { fetchCompanyContact } from 'actions/companyContact';
import { setDefaultPaymentMethod, resetNewPaymentMethod } from 'actions/paymentMethods';
import { OverlayError } from 'components/PaymentConsent/OverlayError';
import { showErrorPopUp } from 'helpers/overlayErrors';

interface Props {
  open: boolean;
  companyId: number;
  onClose(): void;
  onSuccess(): void;
}

export const AddPaymentDialog: React.FC<Props> = ({ open, companyId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const { companyContactData, isFetching: loadingContacts } = useTypedSelector(state => state.companyContact);
  const { newPaymentMethod, isFetching: loadingPayment } = useTypedSelector(state => state.paymentMethods);
  const { billingSystemError } = useTypedSelector(state => state.billingErrors);
  const showPopUp = showErrorPopUp(billingSystemError.data);

  React.useEffect(() => {
    if (companyContactData.id !== companyId && open) {
      dispatch(fetchCompanyContact(companyId));
    }
  }, [dispatch, companyContactData, companyId, open]);

  React.useEffect(() => {
    if (newPaymentMethod.id && open) {
      dispatch(
        setDefaultPaymentMethod({
          companyId: companyId.toString(),
          paymentMethodId: newPaymentMethod.id
        })
      );
      dispatch(resetNewPaymentMethod());
      onSuccess();
      onClose();
    }
  }, [dispatch, newPaymentMethod, companyId, open, onClose, onSuccess]);

  return (
    <Dialog open={open} fullScreen={true} onClose={onClose}>
      <Header />
      {billingSystemError.data && !showPopUp ? (
        <div className={classes.errorContainer}>
          <OverlayError onClose={onClose} />
        </div>
      ) : (
        <div className={classes.dialogContainer}>
          <DialogTitle className={classes.title} disableTypography>
            {t('subscriber:addNewPaymentMethodModal.title', 'Payment')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText className={classes.subtitle}>
              {t('subscriber:addNewPaymentMethodModal.paymentMethodHeader', 'Type of Payment Method')}
            </DialogContentText>
            <DialogContentText className={classes.descriptionInfo}>
              {t(
                'subscriber:addNewPaymentMethodModal.paymentMethodDescription',
                'Select the type of payment method and provide payment method details.'
              )}
            </DialogContentText>
            {open && <AddNewPaymentMethod contactData={companyContactData} loading={loadingContacts} />}
          </DialogContent>
          <DialogActions className={classes.actionsContainer}>
            <Button color="primary" onClick={onClose} disabled={loadingPayment}>
              {t('subscriber:common.close', 'Close')}
            </Button>
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    dialogContainer: {
      width: '768px',
      margin: 'auto'
    },
    errorContainer: {
      margin: '20px auto auto',
      width: '768px'
    },
    title: {
      letterSpacing: 'var(--character-spacing-0)',
      font: 'var(--font-style-normal) normal var(--font-weight-normal) var(--font-size-32) var(--font-family-roboto)'
    },
    subtitle: {
      color: 'var(--on-surface-medium-emphasis-000000-87-)',
      letterSpacing: 'var(--character-spacing-0-4)',
      font:
        'var(--font-style-normal) normal var(--font-weight-normal) var(--font-size-20)/var(--line-spacing-24) var(--font-family-roboto)'
    },
    descriptionInfo: {
      color: 'var(--on-surface-medium-emphasis-000000-87-)',
      letterSpacing: 'var(--character-spacing-0-5)',
      font:
        'var(--font-style-normal) normal var(--font-weight-normal) var(--font-size-14)/var(--line-spacing-20) var(--font-family-roboto)'
    },
    actionsContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      padding: '0 0 20px'
    }
  })
);
